
import { useState } from "react";
import { ChevronDown, ChevronRight, Sliders } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { InlineField } from "@/components/ui/inline-field";
import { InlineDateField } from "@/components/ui/inline-date-field";
import { InlineSelectField } from "@/components/ui/inline-select-field";
import { CustomFieldType, type CustomFieldDefinition, type ChecklistItem } from "@/types/crm";

interface DealCustomFieldsCardProps {
  dealTypeName: string;
  fields: CustomFieldDefinition[];
  values: Record<string, unknown>;
  userOptions: { value: string; label: string }[];
  resolveUserName: (id: string) => string;
  onSaveField: (fieldName: string, value: unknown) => void;
}

function BooleanField({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start">
      <div className="flex-1 min-w-0">
        <p className="text-xs text-muted-foreground mb-0.5">{label}</p>
        <label className="flex items-center gap-2 cursor-pointer rounded-sm hover:bg-accent/40 -mx-1 px-1 py-0.5 transition-colors w-full">
          <Checkbox
            checked={checked}
            onCheckedChange={(v) => onChange(Boolean(v))}
          />
          <span className="text-sm select-none">{checked ? "Yes" : "No"}</span>
        </label>
      </div>
    </div>
  );
}

function ReadOnlyField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start">
      <div className="flex-1 min-w-0">
        <p className="text-xs text-muted-foreground mb-0.5">{label}</p>
        <div className="py-0.5">{children}</div>
      </div>
    </div>
  );
}

export function DealCustomFieldsCard({
  dealTypeName,
  fields,
  values,
  userOptions,
  resolveUserName,
  onSaveField,
}: DealCustomFieldsCardProps) {
  const [open, setOpen] = useState(true);

  if (fields.length === 0) return null;

  const renderField = (field: CustomFieldDefinition) => {
    const raw = values[field.name];

    switch (field.type) {
      case CustomFieldType.Text:
        return (
          <InlineField
            label={field.name}
            value={raw != null ? String(raw) : ""}
            onSave={(v) => onSaveField(field.name, v || null)}
            emptyText={`Add ${field.name.toLowerCase()}…`}
          />
        );

      case CustomFieldType.Textarea:
        return (
          <InlineField
            label={field.name}
            value={raw != null ? String(raw) : ""}
            onSave={(v) => onSaveField(field.name, v || null)}
            multiline
            emptyText={`Add ${field.name.toLowerCase()}…`}
          />
        );

      case CustomFieldType.Number:
        return (
          <InlineField
            label={field.name}
            value={raw != null ? String(raw) : ""}
            onSave={(v) => onSaveField(field.name, v !== "" ? parseFloat(v) : null)}
            emptyText="0"
          />
        );

      case CustomFieldType.Date:
        return (
          <InlineDateField
            label={field.name}
            value={raw != null ? String(raw) : null}
            onSave={(v) => onSaveField(field.name, v || null)}
          />
        );

      case CustomFieldType.Boolean:
        return (
          <BooleanField
            label={field.name}
            checked={Boolean(raw)}
            onChange={(v) => onSaveField(field.name, v)}
          />
        );

      case CustomFieldType.Select:
        return (
          <InlineSelectField
            label={field.name}
            value={raw != null ? String(raw) : null}
            options={(field.options ?? []).map((o) => ({ value: o, label: o }))}
            onSave={(v) => onSaveField(field.name, v || null)}
            placeholder="Select…"
          />
        );

      case CustomFieldType.Member:
        return (
          <InlineSelectField
            label={field.name}
            value={raw != null ? String(raw) : null}
            options={userOptions}
            onSave={(v) => onSaveField(field.name, v || null)}
            placeholder="Select member…"
          />
        );

      case CustomFieldType.Multiselect: {
        const selected: string[] = Array.isArray(raw) ? (raw as string[]) : [];
        return (
          <ReadOnlyField label={field.name}>
            {selected.length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {selected.map((v) => (
                  <Badge key={v} variant="secondary" className="text-xs h-5">
                    {v}
                  </Badge>
                ))}
              </div>
            ) : (
              <span className="text-sm text-muted-foreground/50 italic">None selected</span>
            )}
          </ReadOnlyField>
        );
      }

      case CustomFieldType.Members: {
        const ids: string[] = Array.isArray(raw) ? (raw as string[]) : [];
        return (
          <ReadOnlyField label={field.name}>
            {ids.length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {ids.map((id) => (
                  <Badge key={id} variant="secondary" className="text-xs h-5">
                    {resolveUserName(id)}
                  </Badge>
                ))}
              </div>
            ) : (
              <span className="text-sm text-muted-foreground/50 italic">None selected</span>
            )}
          </ReadOnlyField>
        );
      }

      case CustomFieldType.Checklist: {
        const items: ChecklistItem[] = Array.isArray(raw) ? (raw as ChecklistItem[]) : [];
        const done = items.filter((i) => i.checked).length;
        return (
          <ReadOnlyField label={field.name}>
            {items.length > 0 ? (
              <div className="space-y-1">
                <p className="text-[10px] text-muted-foreground mb-1.5">
                  {done}/{items.length} completed
                </p>
                {items.map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <Checkbox checked={item.checked} disabled className="shrink-0" />
                    <span className={item.checked ? "line-through text-muted-foreground/50" : ""}>
                      {item.text}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <span className="text-sm text-muted-foreground/50 italic">Empty checklist</span>
            )}
          </ReadOnlyField>
        );
      }

      default:
        return null;
    }
  };

  return (
    <Card>
      <CardHeader
        className="pb-2 cursor-pointer select-none"
        onClick={() => setOpen((o) => !o)}
      >
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-1.5">
            <Sliders className="h-4 w-4 text-violet-500" />
            {dealTypeName} Fields
            <span className="text-xs font-normal text-muted-foreground">({fields.length})</span>
          </CardTitle>
          {open ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
      </CardHeader>
      {open && (
        <CardContent className="space-y-3 pt-0">
          {fields.map((field) => (
            <div key={field.name}>{renderField(field)}</div>
          ))}
          <p className="text-[10px] text-muted-foreground/40 italic pt-1">
            Multiselect, members &amp; checklists: edit via the pencil button above.
          </p>
        </CardContent>
      )}
    </Card>
  );
}
